import React, {useRef, useEffect, useCallback} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import toast from 'react-hot-toast';
import {Loader2, PhoneOff, Mic, MicOff} from 'lucide-react';

import {useUserStore} from '../../store/userStore';
import {useWebSocket} from '../../hooks/useWebSocket';
import {useWebRTC} from '../../hooks/useWebRTC';
import {useAudioRecorder} from '../../hooks/useAudioRecorder';
import {TRANSCRIPTION_RATE} from "../../constants/transcription";

// Hooks
import {
    useRoomState,
    useCallControls,
    useMessageHandler,
    useTranscriptions,
    useRoomActions
} from '../../hooks/Room';

// Components
import RoomHeader from '../../components/Room/Header';
import CallControlsContainer from '../../components/Room/CallControls';
import TranscriptionsContainer from '../../components/Room/Transcriptions';
import SettingsSidebar from '../../components/Room/Settings';
import SpeakingIndicator from '../../components/Room/Audio/SpeakingIndicator';
import RemoteAudio from '../../components/Room/Audio/RemoteAudio';
import useTranscriptionStore from "../../store/transcriptionStore";

const Room = () => {
    const {roomCode} = useParams();
    const navigate = useNavigate();
    const {userId, userName, clearUser} = useUserStore();

    // transcription store
    const {
        getTranscriptions,
        addTranscription,
        clearRoom,
        getRoomStats,
    } = useTranscriptionStore();

    // Refs
    const remoteAudioRef = useRef(null);
    const transcriptionsEndRef = useRef(null);
    const transcriptionsContainerRef = useRef(null);
    const previousRtcStatusRef = useRef('disconnected');
    const joinTimeoutRef = useRef(null);

    // State management
    const roomState = useRoomState();
    const {
        isMuted, setIsMuted,
        isCallEnding, setIsCallEnding,
        participants, setParticipants,
        otherParticipant, setOtherParticipant,
        otherParticipantMuted, setOtherParticipantMuted,
        transcriptions, setTranscriptions,
        showSettings, setShowSettings,
        copiedCode, setCopiedCode,
        myChannel, setMyChannel,
        isRemoteAudioMuted, setIsRemoteAudioMuted,
        settings, setSettings
    } = roomState;

    // WebSocket connection
    const {
        connectionState,
        messages,
        sendMessage,
        disconnect,
        registerWebRTCHandler,
    } = useWebSocket(roomCode, userId);

    // Remote stream handler
    const handleRemoteStream = useCallback((stream) => {
        if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = stream;
        }
    }, []);

    // WebRTC connection
    const {
        status: rtcStatus,
        localStream,
        handleWebRTCMessage,
        makeCall,
        endCall: endWebRTCCall
    } = useWebRTC(
        roomCode,
        userId,
        handleRemoteStream,
        sendMessage
    );

    // Determine if call is truly connected via WebRTC
    const isCallConnected = rtcStatus === 'connected';

    // Audio metadata for transcription
    const audioMetadata = {
        channel: myChannel !== null ? myChannel : 0,
        translate: settings.translate,
        diarise: settings.diarise,
        lang_code: settings.sourceLang,
        target_lang: settings.targetLang,
        room_code: roomCode,
        user_id: userId,
        sample_rate: TRANSCRIPTION_RATE
    };

    // Audio recorder
    const {isSpeaking, audioStats} = useAudioRecorder(
        localStream,
        isMuted,
        isCallConnected,
        audioMetadata,
        sendMessage
    );

    // Register WebRTC handler
    useEffect(() => {
        registerWebRTCHandler(handleWebRTCMessage);
    }, [registerWebRTCHandler, handleWebRTCMessage]);

    // Transcriptions handler
    const {handleTranscription} = useTranscriptions({
        transcriptions,
        setTranscriptions,
        participants,
        userId,
        settings,
        addTranscriptionToStore: (transcription) => {
            addTranscription(roomCode, transcription);
        }
    });

    // Message handler
    useMessageHandler({
        messages,
        userId,
        myChannel,
        rtcStatus,
        endWebRTCCall,
        setParticipants,
        setOtherParticipant,
        setMyChannel,
        setOtherParticipantMuted,
        handleTranscription,
        sendMessage,
        roomCode
    });

    // Call controls
    const {
        initiateCall,
        endCall,
        toggleMute,
        toggleRemoteAudio
    } = useCallControls({
        rtcStatus,
        localStream,
        remoteAudioRef,
        isMuted,
        setIsMuted,
        isCallEnding,
        setIsCallEnding,
        isRemoteAudioMuted,
        setIsRemoteAudioMuted,
        otherParticipant,
        makeCall,
        endWebRTCCall,
        sendMessage,
        userId,
        userName
    });

    // Room actions
    const {
        leaveRoom,
        handleLogout,
        updateSettings,
        copyRoomCode,
        clearHistory
    } = useRoomActions({
        sendMessage,
        disconnect,
        navigate,
        clearUser,
        roomCode,
        setCopiedCode,
        setSettings,
        userId,
        onClearHistory: () => {
            if (window.confirm('Clear all transcriptions for this room? This cannot be undone.')) {
                clearRoom(roomCode);
                setTranscriptions([]);
                toast.success('Transcription history cleared', {position: 'bottom-center'});
            }
        }
    });

    // Load persisted transcriptions on mount
    useEffect(() => {
        const persistedTranscriptions = getTranscriptions(roomCode);
        if (persistedTranscriptions.length > 0) {
            console.log(`[Room] 📚 Loaded ${persistedTranscriptions.length} persisted transcriptions`);
            setTranscriptions(persistedTranscriptions);

            const stats = getRoomStats(roomCode);
            if (stats) {
                toast.success(
                    `Restored ${stats.total} message${stats.total > 1 ? 's' : ''} from previous session`,
                    {position: 'bottom-center', duration: 3000, icon: '📚'}
                );
            }
        }
    }, [roomCode, getTranscriptions, getRoomStats, setTranscriptions]);

    // Show success toast when connected
    useEffect(() => {
        const wasDisconnected = previousRtcStatusRef.current !== 'connected';
        const isNowConnected = rtcStatus === 'connected';

        if (wasDisconnected && isNowConnected && otherParticipant) {
            toast.success(`Connected to ${otherParticipant.name}`, {
                position: 'bottom-center',
                duration: 2000,
                icon: '📞'
            });
        }

        previousRtcStatusRef.current = rtcStatus;
    }, [rtcStatus, otherParticipant]);

    // Ensure muted on mount
    useEffect(() => {
        if (localStream) {
            localStream.getAudioTracks().forEach((track) => {
                track.enabled = false;
                console.log('[Room] Initial mute - track enabled:', track.enabled);
            });
        }

    }, [localStream]);

    // Request participants if missing
    useEffect(() => {
        if (connectionState !== 'connected') return;
        joinTimeoutRef.current = setTimeout(() => {
            if (participants.length < 2) {
                console.log('[Room] 🔄 Requesting initial participant list...');
                sendMessage({
                    type: 'request_participants',
                    user_id: userId,
                    user_name: userName,
                    room_code: roomCode,
                    timestamp: new Date().toISOString()
                });
            }
        }, 30000);
    }, [connectionState, participants.length, sendMessage, userId, userName, roomCode]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (joinTimeoutRef.current) {
                clearTimeout(joinTimeoutRef.current);
            }
        };
    }, []);

    // Get speaking user
    const getSpeakingUser = () => {
        if (!isCallConnected) return null;
        if (isSpeaking && !isMuted) {
            return {name: 'You', isSelf: true};
        }
        return null;
    };

    const speakingUser = getSpeakingUser();

    // Status Bar Component
    const StatusBar = () => (
        <div
            className="glass-panel border-t border-white/10 px-3 sm:px-6 py-2 sm:py-2.5 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2 text-xs sm:text-sm text-zinc-400">
                {!isCallConnected ? (
                    <>
                        <PhoneOff className="w-3 h-3 sm:w-4 sm:h-4"/>
                        <span className="hidden sm:inline">Not in call</span>
                        <span className="sm:hidden">No call</span>
                    </>
                ) : isMuted ? (
                    <>
                        <MicOff className="w-3 h-3 sm:w-4 sm:h-4 text-red-400"/>
                        <span className="hidden sm:inline">You are muted</span>
                        <span className="sm:hidden">Muted</span>
                    </>
                ) : isSpeaking ? (
                    <>
                        <Mic className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 animate-pulse"/>
                        <span className="text-green-400">Speaking...</span>
                    </>
                ) : (
                    <>
                        <Mic className="w-3 h-3 sm:w-4 sm:h-4 text-green-400"/>
                        <span className="hidden sm:inline">Ready to speak</span>
                        <span className="sm:hidden">Ready</span>
                    </>
                )}
            </div>

            {isCallConnected && (
                <button
                    onClick={toggleMute}
                    className={`px-3 sm:px-4 py-1 sm:py-1.5 rounded-lg transition-all text-xs sm:text-sm font-medium ${
                        isMuted
                            ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                            : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    }`}
                >
                    {isMuted ? 'Unmute' : 'Mute'}
                </button>
            )}
        </div>
    );

    return (
        <div className="h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-black flex flex-col">
            {/* Header */}
            <RoomHeader
                roomCode={roomCode}
                copiedCode={copiedCode}
                copyRoomCode={copyRoomCode}
                userName={userName}
                myChannel={myChannel}
                isMuted={isMuted}
                otherParticipant={otherParticipant}
                otherParticipantMuted={otherParticipantMuted}
                isCallConnected={isCallConnected}
                connectionState={connectionState}
                rtcStatus={rtcStatus}
                showSettings={showSettings}
                setShowSettings={setShowSettings}
                leaveRoom={leaveRoom}
                handleLogout={handleLogout}
                participants={participants}
                clearHistory={clearHistory}
            />

            <div className="flex-1 flex min-h-0 flex-col md:flex-row">
                {/* Main Chat Area */}
                <div className="flex-1 flex flex-col min-h-0">
                    {/* Participant Header */}
                    <div
                        className="glass-panel border-b border-white/10 px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between flex-shrink-0">
                        {otherParticipant ? (
                            <>
                                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                    <SpeakingIndicator
                                        speakingUser={speakingUser}
                                        isCallConnected={isCallConnected}
                                        otherParticipantName={otherParticipant?.name}
                                    />
                                </div>

                                <CallControlsContainer
                                    isCallConnected={isCallConnected}
                                    rtcStatus={rtcStatus}
                                    otherParticipant={otherParticipant}
                                    initiateCall={initiateCall}
                                    toggleRemoteAudio={toggleRemoteAudio}
                                    isRemoteAudioMuted={isRemoteAudioMuted}
                                    toggleMute={toggleMute}
                                    isMuted={isMuted}
                                    isSpeaking={isSpeaking}
                                    endCall={endCall}
                                    isCallEnding={isCallEnding}
                                />
                            </>
                        ) : (
                            <div className="flex items-center gap-2 sm:gap-3 text-zinc-400 w-full justify-center py-2">
                                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin"/>
                                <div className="text-center">
                                    <div className="text-xs sm:text-sm font-medium text-white">Waiting for
                                        participant...
                                    </div>
                                    <div className="text-[10px] sm:text-xs mt-0.5">
                                        Share code{' '} <span
                                        className="font-mono text-orange-400">{roomCode}</span> with someone to
                                        start
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Chat Messages Area */}
                    <TranscriptionsContainer
                        transcriptions={transcriptions}
                        transcriptionsContainerRef={transcriptionsContainerRef}
                        transcriptionsEndRef={transcriptionsEndRef}
                        isCallConnected={isCallConnected}
                        isMuted={isMuted}
                    />

                    {/* Status Bar */}
                    <StatusBar/>
                </div>

                {/* Sidebar - Settings & Participants */}
                {showSettings && (
                    <div className="fixed md:relative inset-0 md:inset-auto z-50 md:z-auto">
                        <SettingsSidebar
                            participants={participants}
                            userId={userId}
                            myChannel={myChannel}
                            isMuted={isMuted}
                            otherParticipantMuted={otherParticipantMuted}
                            isCallConnected={isCallConnected}
                            settings={settings}
                            updateSettings={updateSettings}
                            transcriptions={transcriptions}
                            audioStats={audioStats}
                            roomStats={getRoomStats(roomCode)}
                            onClearHistory={clearHistory}
                            onClose={() => setShowSettings(false)}
                        />
                    </div>
                )}
            </div>

            {/* Remote Audio Element */}
            <RemoteAudio ref={remoteAudioRef}/>
        </div>
    );
};

export default Room;